import { createApi, createBaseQuery } from '@adv-frontend/modules-query';
import { apiV1Request } from '#shared/api/requests';
import {
    type OpenApiCreateMenuRequest,
    type OpenApiCreateMenuResponseSuccess,
    type OpenApiDeleteMenuPathParameters,
    type OpenApiDeleteMenuResponseSuccess,
    type OpenApiGetMenuLinksRequest,
    type OpenApiGetMenuLinksResponseSuccess,
    type OpenApiGetMenuPathParameters,
    type OpenApiGetMenuResponseSuccess,
    type OpenApiGetMenusRequest,
    type OpenApiGetMenusResponseSuccess,
    type OpenApiSchemaMenu,
    type OpenApiSchemaMenuPossibleCategoryLink,
    type OpenApiSchemaMenuPossiblePageLink,
    type OpenApiUpdateMenuPathParameters,
    type OpenApiUpdateMenuRequest,
    type OpenApiUpdateMenuResponseSuccess,
} from '#shared/api/schema';
import { mapMenuItemToSaving } from '../lib';

const MenuListTag = 'MenuList' as const;
const MenuTag = 'Menu' as const;
const LinksTag = 'LinksTag' as const;
const LinkCategoryTag = 'LinkCategory' as const;
const LinkPageTag = 'LinkPage' as const;

const sortMenusByCreatedAt = (menus: OpenApiGetMenusResponseSuccess): OpenApiGetMenusResponseSuccess =>
    [...menus].sort((firstMenu, secondMenu) => {
        const createdAtOrder = firstMenu.created_at.localeCompare(secondMenu.created_at);

        if (createdAtOrder !== 0) {
            return createdAtOrder;
        }

        return firstMenu.id.localeCompare(secondMenu.id);
    });

const appendMenuToList = (
    menus: readonly OpenApiSchemaMenu[],
    menuToAppend: OpenApiSchemaMenu
): OpenApiGetMenusResponseSuccess => [...menus.filter(({ id }) => id !== menuToAppend.id), menuToAppend];

const replaceMenuInList = (
    menus: readonly OpenApiSchemaMenu[],
    menuToReplace: OpenApiSchemaMenu
): OpenApiGetMenusResponseSuccess => menus.map((menu) => (menu.id === menuToReplace.id ? menuToReplace : menu));

const removeMenuFromList = (
    menus: readonly OpenApiSchemaMenu[],
    menuId: OpenApiSchemaMenu['id']
): OpenApiGetMenusResponseSuccess => menus.filter(({ id }) => id !== menuId);

const getMenuTag = ({ id }: Pick<OpenApiSchemaMenu, 'id'>) => {
    return { type: MenuTag, id };
};

const getLinkCategoryTag = ({ id }: Pick<OpenApiSchemaMenuPossibleCategoryLink, 'id'>) => {
    return { type: LinkCategoryTag, id };
};

const getLinkPageTag = ({ id }: Pick<OpenApiSchemaMenuPossiblePageLink, 'id'>) => {
    return { type: LinkPageTag, id };
};

export const apiMenu = createApi({
    name: 'entities/menu/api/index',
    tagTypes: [MenuListTag, MenuTag, LinkPageTag, LinkCategoryTag, LinksTag],
    baseQuery: createBaseQuery(apiV1Request),
    endpoints: (builder) => {
        return {
            getList: builder.query<OpenApiGetMenusResponseSuccess, OpenApiGetMenusRequest>({
                providesTags: (result, error) => {
                    if (error || !result) {
                        return [];
                    }

                    return [MenuListTag];
                },
                query: () => {
                    return { url: '/menu', method: 'GET' };
                },
                transformResponse: sortMenusByCreatedAt,
            }),
            getById: builder.query<OpenApiGetMenuResponseSuccess, OpenApiGetMenuPathParameters>({
                query: ({ menu_id }) => {
                    return { url: `/menu/${menu_id}`, method: 'GET' };
                },
                providesTags: (result, error) => {
                    if (error || !result) {
                        return [];
                    }

                    return [{ type: MenuTag, id: 'getMenu' }, getMenuTag(result)];
                },
            }),
            create: builder.mutation<OpenApiCreateMenuResponseSuccess, OpenApiCreateMenuRequest>({
                query: ({ title, items }) => {
                    return {
                        url: '/menu',
                        method: 'POST',
                        body: {
                            title,
                            items: items.map(mapMenuItemToSaving),
                        },
                    };
                },
                async onQueryStarted(_, { queryFulfilled }) {
                    try {
                        const { data: menu } = await queryFulfilled;

                        apiMenu.endpoints.getList.updateQueryData(undefined, (draft) => appendMenuToList(draft, menu));
                        await apiMenu.endpoints.getById.upsertQueryData({ menu_id: menu.id }, menu);
                    } catch (_error) {
                        return;
                    }
                },
            }),
            update: builder.mutation<
                OpenApiUpdateMenuResponseSuccess,
                { params: OpenApiUpdateMenuPathParameters; payload: OpenApiUpdateMenuRequest }
            >({
                query: ({ params: { menu_id }, payload: { title, items } }) => {
                    return {
                        url: `/menu/${menu_id}`,
                        method: 'PUT',
                        body: {
                            title,
                            items: items.map(mapMenuItemToSaving),
                        },
                    };
                },
                invalidatesTags: (_result, error, request) => {
                    if (error) {
                        return [];
                    }

                    return [getMenuTag({ id: request.params.menu_id })];
                },
                async onQueryStarted(_, { queryFulfilled }) {
                    try {
                        const { data: menu } = await queryFulfilled;

                        apiMenu.endpoints.getList.updateQueryData(undefined, (draft) => replaceMenuInList(draft, menu));
                        await apiMenu.endpoints.getById.upsertQueryData({ menu_id: menu.id }, menu);
                    } catch (_error) {
                        return;
                    }
                },
            }),
            delete: builder.mutation<OpenApiDeleteMenuResponseSuccess, OpenApiDeleteMenuPathParameters>({
                query: ({ menu_id }) => {
                    return {
                        url: `/menu/${menu_id}`,
                        method: 'DELETE',
                    };
                },
                invalidatesTags: (_result, error, { menu_id: id }) => {
                    if (error) {
                        return [];
                    }

                    return [getMenuTag({ id })];
                },
                async onQueryStarted({ menu_id: id }, { queryFulfilled }) {
                    try {
                        await queryFulfilled;

                        apiMenu.endpoints.getList.updateQueryData(undefined, (draft) => removeMenuFromList(draft, id));
                    } catch (_error) {
                        return;
                    }
                },
            }),
            getLinks: builder.query<OpenApiGetMenuLinksResponseSuccess, OpenApiGetMenuLinksRequest>({
                query: () => {
                    return {
                        url: '/menu/links',
                        method: 'GET',
                    };
                },
                providesTags: (result, error) => {
                    if (error || !result) {
                        return [];
                    }

                    return [LinksTag, ...result.category.map(getLinkCategoryTag), ...result.page.map(getLinkPageTag)];
                },
            }),
        };
    },
});

export const useGetMenuList = apiMenu.useGetListQuery;
export const useGetMenuLinks = apiMenu.useGetLinksQuery;
export const useGetMenuById = apiMenu.useLazyGetByIdQuery;
export const getMenuList = apiMenu.endpoints.getList.query;
export const updateMenu = apiMenu.endpoints.update.mutate;
export const createMenu = apiMenu.endpoints.create.mutate;
export const useDeleteMenu = apiMenu.useDeleteMutation;
